import React, { useState, useEffect } from 'react';
import { Typography, Card, Form, Input, Button, message, Space, Select, InputNumber, Table, Layout } from 'antd';
import { SaveOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useOutletContext } from 'react-router-dom';
import { settingsService, CurrencySettings } from '../../services/settings';

const { Text } = Typography;
const { Option } = Select;
const { Content } = Layout;
type LayoutHeaderContext = {
  setHeaderContent: (content: React.ReactNode) => void;
};

export const CurrencySettingsPage: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [currencyData, setCurrencyData] = useState<CurrencySettings | null>(null);
  const [exchangeRates, setExchangeRates] = useState<Array<{currency: string, rate: number}>>([]);
  const { setHeaderContent } = useOutletContext<LayoutHeaderContext>();

  useEffect(() => {
    fetchCurrencySettings();
  }, []);

  useEffect(() => {
    setHeaderContent(
      <span style={{ fontSize: 16, fontWeight: 600, color: '#111827' }}>
        Currency Settings
      </span>
    );
    return () => setHeaderContent(null);
  }, [setHeaderContent]);

  const fetchCurrencySettings = async () => {
    try {
      setLoading(true);
      const data = await settingsService.getCurrencySettings();
      setCurrencyData(data);
      form.setFieldsValue(data);
      
      // Convert exchange rates to array format for table
      const ratesArray = Object.entries(data.exchange_rates).map(([currency, rate]) => ({
        currency,
        rate
      }));
      setExchangeRates(ratesArray);
    } catch (error) {
      message.error('Failed to load currency settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (values: CurrencySettings) => {
    try {
      setLoading(true);
      
      // Convert exchange rates array back to object
      const exchangeRatesObj = exchangeRates.reduce((acc, item) => {
        acc[item.currency] = item.rate;
        return acc;
      }, {} as Record<string, number>);
      
      const updatedValues = {
        ...values,
        exchange_rates: exchangeRatesObj
      };
      
      await settingsService.updateCurrencySettings(updatedValues);
      message.success('Currency settings updated successfully');
      setCurrencyData(updatedValues);
    } catch (error) {
      message.error('Failed to update currency settings');
    } finally {
      setLoading(false);
    }
  };

  const addExchangeRate = () => {
    setExchangeRates([...exchangeRates, { currency: 'USD', rate: 1.0 }]);
  };

  const removeExchangeRate = (index: number) => {
    const newRates = exchangeRates.filter((_, i) => i !== index);
    setExchangeRates(newRates);
  };

  const updateExchangeRate = (index: number, field: string, value: any) => {
    const newRates = [...exchangeRates];
    newRates[index] = { ...newRates[index], [field]: value };
    setExchangeRates(newRates);
  };

  const exchangeRateColumns = [
    {
      title: 'Currency',
      dataIndex: 'currency',
      key: 'currency',
      render: (value: string, record: any, index: number) => (
        <Select
          value={value}
          onChange={(val) => updateExchangeRate(index, 'currency', val)}
          style={{ width: '100%' }}
        >
          <Option value="USD">USD - US Dollar</Option>
          <Option value="SGD">SGD - Singapore Dollar</Option>
          <Option value="EUR">EUR - Euro</Option>
          <Option value="GBP">GBP - British Pound</Option>
          <Option value="JPY">JPY - Japanese Yen</Option>
          <Option value="AUD">AUD - Australian Dollar</Option>
          <Option value="CAD">CAD - Canadian Dollar</Option>
          <Option value="CHF">CHF - Swiss Franc</Option>
        </Select>
      ),
    },
    {
      title: 'Exchange Rate',
      dataIndex: 'rate',
      key: 'rate',
      render: (value: number, record: any, index: number) => (
        <InputNumber
          value={value}
          onChange={(val) => updateExchangeRate(index, 'rate', val)}
          min={0}
          step={0.01}
          precision={4}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (value: any, record: any, index: number) => (
        <Button
          type="link"
          danger
          icon={<DeleteOutlined />}
          onClick={() => removeExchangeRate(index)}
        >
          Remove
        </Button>
      ),
    },
  ];

  return (
    <Content style={{ padding: '24px' }}>
      <Text type="secondary">Configure base currency and exchange rates for multi-currency support</Text>
      
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSave}
        style={{ marginTop: '24px' }}
      >
        <Card title="Base Currency Settings" style={{ marginBottom: '24px' }}>
          <Form.Item
            name="base_currency"
            label="Base Currency"
            rules={[{ required: true, message: 'Please select base currency' }]}
          >
            <Select placeholder="Select base currency">
              <Option value="MYR">MYR - Malaysian Ringgit</Option>
              <Option value="USD">USD - US Dollar</Option>
              <Option value="SGD">SGD - Singapore Dollar</Option>
              <Option value="EUR">EUR - Euro</Option>
              <Option value="GBP">GBP - British Pound</Option>
            </Select>
          </Form.Item>
          
          <Form.Item
            name="currency_symbol"
            label="Currency Symbol"
            rules={[{ required: true, message: 'Please enter currency symbol' }]}
          >
            <Input placeholder="RM" />
          </Form.Item>
          
          <Form.Item
            name="decimal_places"
            label="Decimal Places"
            rules={[{ required: true, message: 'Please enter decimal places' }]}
          >
            <InputNumber min={0} max={4} placeholder="2" style={{ width: '100%' }} />
          </Form.Item>
        </Card>

        <Card title="Exchange Rates" style={{ marginBottom: '24px' }}>
          <div style={{ marginBottom: '16px' }}>
            <Button type="dashed" icon={<PlusOutlined />} onClick={addExchangeRate}>
              Add Exchange Rate
            </Button>
          </div>
          
          <Table
            columns={exchangeRateColumns}
            dataSource={exchangeRates}
            rowKey={(record, index) => index || 0}
            pagination={false}
            size="small"
          />
        </Card>

        <Space>
          <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={loading}>
            Save Currency Settings
          </Button>
          <Button onClick={() => form.resetFields()}>
            Reset
          </Button>
        </Space>
      </Form>
    </Content>
  );
};
